import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";
import { api, apiError, API } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, History, Upload, Loader2, X, AlertTriangle, Sparkles, Trash2 } from "lucide-react";
import { CreateByAIModal } from "@/components/CreateByAIModal";
import { YouTubeCreateConnect } from "@/components/YouTubeCreateConnect";
import AssetTagChips from "@/components/AssetTagChips";

const EMPTY = {
  name: "", niche: "", description: "", target_country: "", target_audience: "English-speaking countries (US/UK/CA/AU)",
  primary_language: "en-US", subtitle_languages: [],
  tone: "", brand_voice: "", terminology_notes: "", pronunciation_notes: "",
  restricted_topics: "", editorial_instructions: "",
  logo_url: "", brand_colors: ["#8B5CF6", "#38BDF8"], fonts: "", thumbnail_style: "", visual_references: [],
  default_format: "short", duration_target: 45, aspect_ratio: "9:16", publishing_cadence: "", timezone: "UTC",
  competitor_channels: [], research_sources: [],
  budget_per_video: 0, monthly_budget: 0, currency: "USD",
  require_human_approval: true, auto_research: false, auto_script: false, auto_publish: false,
  idea_radar_enabled: false, radar_weekday: 0, radar_hour: 9, radar_count: 3,
};

const LOCALES = ["en-US", "en-GB", "fr-FR", "es-US", "es-ES", "de-DE", "pt-BR", "az-AZ", "tr-TR", "ru-RU", "ar-SA", "hi-IN", "ja-JP"];
const TIMEZONES = ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Baku", "Asia/Istanbul", "Asia/Tokyo"];

function Section({ title, desc, children, testid }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-6 mb-5" data-testid={testid}>
      <div className="mb-5">
        <h3 className="text-lg font-display font-semibold text-slate-100">{title}</h3>
        {desc && <p className="text-sm text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300 text-sm mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

function ListField({ value, onChange, placeholder }) {
  return (
    <Input className={inputCls} placeholder={placeholder}
      value={(value || []).join(", ")}
      onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
  );
}

export default function ChannelEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { loadChannels } = useStudio();
  const [data, setData] = useState(isNew ? EMPTY : null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const [nicheDup, setNicheDup] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [delPreview, setDelPreview] = useState(null);
  const [brandAssets, setBrandAssets] = useState([]);
  const fileRef = useRef();

  // Live, debounced niche-discipline check → renders a non-blocking amber banner.
  useEffect(() => {
    const niche = (data?.niche || "").trim();
    if (!niche) { setNicheDup(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data: chk } = await api.get("/channels/check-niche", {
          params: { niche, language: data.primary_language, exclude_id: id || undefined },
        });
        setNicheDup(chk.duplicate ? chk.existing : null);
      } catch { setNicheDup(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [data?.niche, data?.primary_language, id]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data: ch } = await api.get(`/channels/${id}`);
        setData({ ...EMPTY, ...ch.data });
        setMeta(ch);
      } catch (e) { setError(apiError(e)); }
    })();
  }, [id, isNew]);

  const loadBrand = useCallback(async () => {
    if (isNew || !id) return;
    try { const { data: a } = await api.get(`/channels/${id}/assets?kind=brand_reference`); setBrandAssets(a); }
    catch { setBrandAssets([]); }
  }, [id, isNew]);
  useEffect(() => { loadBrand(); }, [loadBrand]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const loadVersions = async () => {
    try { const { data: v } = await api.get(`/channels/${id}/versions`); setVersions(v); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doCreate = async () => {
    setSaving(true);
    try {
      const { data: ch } = await api.post("/channels", data);
      toast.success("Channel created");
      await loadChannels();
      navigate(`/channels/${ch.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!data.name.trim()) { toast.error("Channel name is required"); return; }
    if (isNew) {
      // Niche discipline: warn (don't block) if an active channel already covers this niche+language.
      try {
        const { data: chk } = await api.get("/channels/check-niche", {
          params: { niche: data.niche, language: data.primary_language },
        });
        if (chk.duplicate) { setDupWarn(chk.existing); return; }
      } catch (e) { logger.error("niche check failed (advisory)", e); /* advisory — never block creation on its failure */ }
      await doCreate();
      return;
    }
    setSaving(true);
    try {
      const { data: ch } = await api.put(`/channels/${id}`, data);
      setMeta(ch);
      toast.success(`Saved · now version ${ch.version}`);
      await loadChannels();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", res.url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const openDelete = async () => {
    setDelConfirm(""); setDelPreview(null); setDelOpen(true);
    try { const { data } = await api.get(`/channels/${id}/delete-preview`); setDelPreview(data.counts); }
    catch (e) { logger.error("Delete-preview failed", e); }
  };
  const doDelete = async () => {
    setDeleting(true);
    const name = data.name;
    try {
      await api.delete(`/channels/${id}`, { data: { confirm_name: delConfirm } });
      await loadChannels();
      toast.success(`Channel "${name}" deleted`, {
        duration: 60000,
        description: "Permanent in 60s.",
        action: { label: "Undo", onClick: async () => { try { await api.post(`/channels/${id}/undo-delete`); toast.success(`Restored "${name}"`); await loadChannels(); navigate(`/channels/${id}`); } catch (e) { toast.error(apiError(e)); } } },
      });
      navigate("/channels");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!data) return <LoadingState label="Loading channel…" />;

  return (
    <div className="animate-in-up max-w-4xl">
      <CreateByAIModal open={showAI} onOpenChange={setShowAI} onCreated={async () => { await loadChannels(); }} />
      <Dialog open={!!dupWarn} onOpenChange={(o) => !o && setDupWarn(null)}>
        <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-md" data-testid="duplicate-niche-warning">
          <DialogHeader><DialogTitle>Niche already covered</DialogTitle>
            <DialogDescription className="text-slate-400">Advisory only — you can still create this channel.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-300">A channel for this niche/language already exists: <span className="font-semibold text-violet-300">{dupWarn?.name}</span>. The plan is one channel per topic/niche — create anyway?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDupWarn(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="duplicate-cancel">Cancel</Button>
            <Button onClick={() => { setDupWarn(null); doCreate(); }} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="duplicate-create-anyway">Create anyway</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/channels")} className="text-slate-400 hover:text-slate-200" data-testid="back-to-channels"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-2xl font-display font-extrabold text-slate-50">{isNew ? "New channel" : data.name || "Channel"}</h1>
            {meta && <p className="text-xs text-slate-500 mt-0.5">Version {meta.version} · {meta.is_sample ? "Sample profile" : "Custom profile"}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isNew && (
            <Button onClick={() => setShowAI(true)} variant="outline" className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10" data-testid="editor-create-with-ai">
              <Sparkles className="w-4 h-4 mr-1.5" /> Create with AI
            </Button>
          )}
          {!isNew && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={loadVersions} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="version-history-button">
                  <History className="w-4 h-4 mr-1.5" /> History
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-lg">
                <DialogHeader><DialogTitle>Version history</DialogTitle></DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto space-y-3" data-testid="version-history-list">
                  {versions.length === 0 ? <p className="text-sm text-slate-500">No versions.</p> : versions.map((v) => (
                    <div key={v.id} className="border border-slate-800 rounded-lg p-3" data-testid={`version-item-${v.version}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-mono px-2 py-0.5 rounded bg-slate-800 text-violet-300 border border-slate-700">v{v.version}</span>
                        <span className="text-xs text-slate-500">{new Date(v.created_at).toLocaleString()}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {v.change_summary.map((c, i) => <span key={`${v.version}-${i}`} className="text-xs px-2 py-0.5 rounded bg-slate-800/60 text-slate-400">{c}</span>)}
                      </div>
                      <div className="text-xs text-slate-600 mt-1.5">by {v.actor}</div>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          )}
          {!isNew && (
            <Button variant="outline" onClick={openDelete} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="editor-delete-channel">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete…
            </Button>
          )}
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="save-channel-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> {isNew ? "Create" : "Save version"}</>}
          </Button>
        </div>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-[#111827] border-red-500/30 text-slate-100" data-testid="editor-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300"><AlertTriangle className="w-5 h-5" /> Delete channel permanently</DialogTitle>
            <DialogDescription className="text-slate-400">
              This permanently deletes <span className="font-semibold text-slate-200">{data.name}</span> and everything scoped to it — projects, scripts, renders, ideas, playbooks, growth state, budgets, schedules and channel-only styles. Rendered files are erased too. <span className="text-red-300 font-medium">This cannot be undone.</span> Global styles are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            {delPreview && (
              <div className="mb-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-xs text-slate-300" data-testid="editor-delete-preview">
                <p className="text-slate-400 mb-1.5">This will permanently delete:</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><b className="text-slate-100">{delPreview.projects}</b> projects</span>
                  <span><b className="text-slate-100">{delPreview.ideas}</b> ideas</span>
                  <span><b className="text-slate-100">{delPreview.script_versions}</b> scripts</span>
                  <span><b className="text-slate-100">{delPreview.scenes}</b> scenes</span>
                  <span><b className="text-slate-100">{delPreview.renders}</b> renders ({delPreview.render_storage_mb} MB)</span>
                  <span><b className="text-slate-100">{delPreview.schedules}</b> schedules</span>
                  <span><b className="text-slate-100">{delPreview.styles_deleted}</b> channel styles</span>
                </div>
                <p className="text-slate-500 mt-1.5">{delPreview.styles_kept_global} global style(s) kept. You'll have 60s to undo.</p>
              </div>
            )}
            <Label className="text-slate-300 text-sm mb-1.5 block">Type <span className="font-mono text-slate-100">{data.name}</span> to confirm</Label>
            <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} autoFocus placeholder={data.name} data-testid="editor-delete-confirm-input" className="bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-red-500/40" />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="editor-cancel-delete">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting || delConfirm !== data.name} className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40" data-testid="editor-confirm-delete">
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ChannelForm data={data} set={set} nicheDup={nicheDup} fileRef={fileRef} uploadLogo={uploadLogo} uploading={uploading} brandAssets={brandAssets} loadBrand={loadBrand} />

      {!isNew && id && (
        <YouTubeCreateConnect channelId={id} name={data.name}
          handle={data.handle_suggestion || (data.name ? "@" + data.name.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 30) : "")}
          description={data.description} />
      )}

      {!isNew && id && <PublishingSettings channelId={id} />}
    </div>
  );
}

function ChannelForm({ data, set, nicheDup, fileRef, uploadLogo, uploading, brandAssets = [], loadBrand }) {
  return (
    <>
      <Section title="Identity" desc="Who this channel is." testid="section-identity">
        {nicheDup && (
          <div className="sm:col-span-2 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 text-sm text-amber-300" data-testid="niche-duplicate-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
            <span>A channel for this niche/language already exists: <span className="font-semibold">{nicheDup.name}</span>. The plan is one channel per topic/niche — you can still create it if that's intended.</span>
          </div>
        )}
        <Field label="Name"><Input className={inputCls} value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name" /></Field>
        <Field label="Niche"><Input className={inputCls} value={data.niche} onChange={(e) => set("niche", e.target.value)} data-testid="field-niche" /></Field>
        <Field label="Description" full><Textarea className={inputCls} value={data.description} onChange={(e) => set("description", e.target.value)} data-testid="field-description" /></Field>
        <Field label="Target country"><Input className={inputCls} value={data.target_country} onChange={(e) => set("target_country", e.target.value)} /></Field>
        <Field label="Target audience"><Input className={inputCls} value={data.target_audience} onChange={(e) => set("target_audience", e.target.value)} /></Field>
      </Section>

      <Section title="Language" desc="Content and translated metadata languages." testid="section-language">
        <Field label="Primary language / locale">
          <Select value={data.primary_language} onValueChange={(v) => set("primary_language", v)}>
            <SelectTrigger className={inputCls} data-testid="field-primary-language"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{LOCALES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Subtitle / translated languages (comma separated)"><ListField value={data.subtitle_languages} onChange={(v) => set("subtitle_languages", v)} placeholder="es-US, fr-FR" /></Field>
      </Section>

      <Section title="Voice & editorial" desc="How this channel sounds and what it must respect." testid="section-voice">
        <Field label="Tone"><Input className={inputCls} value={data.tone} onChange={(e) => set("tone", e.target.value)} /></Field>
        <Field label="Brand voice"><Input className={inputCls} value={data.brand_voice} onChange={(e) => set("brand_voice", e.target.value)} /></Field>
        <Field label="Terminology notes" full><Textarea className={inputCls} value={data.terminology_notes} onChange={(e) => set("terminology_notes", e.target.value)} /></Field>
        <Field label="Pronunciation notes"><Textarea className={inputCls} value={data.pronunciation_notes} onChange={(e) => set("pronunciation_notes", e.target.value)} /></Field>
        <Field label="Restricted topics"><Textarea className={inputCls} value={data.restricted_topics} onChange={(e) => set("restricted_topics", e.target.value)} /></Field>
        <Field label="Editorial instructions" full><Textarea className={inputCls} value={data.editorial_instructions} onChange={(e) => set("editorial_instructions", e.target.value)} /></Field>
      </Section>

      <Section title="Brand" desc="Visual identity." testid="section-brand">
        <Field label="Logo">
          <div className="flex items-center gap-3">
            {data.logo_url ? (
              <div className="relative">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${data.logo_url}`} alt="logo" className="w-14 h-14 rounded-lg object-cover border border-slate-700" data-testid="logo-preview" />
                <button onClick={() => set("logo_url", "")} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center"><X className="w-3 h-3 text-slate-300" /></button>
              </div>
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-800 border border-dashed border-slate-700 flex items-center justify-center text-slate-500 text-xs">None</div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="logo-upload-button">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload</>}
            </Button>
          </div>
        </Field>
        <Field label="Brand colors">
          <div className="flex items-center gap-2">
            {/* index key is intentional: positional color swatches edited in place by index (documented round 2) */}
            {(data.brand_colors || []).map((c, i) => (
              <input key={i} type="color" value={c} data-testid={`brand-color-${i}`}
                onChange={(e) => { const arr = [...data.brand_colors]; arr[i] = e.target.value; set("brand_colors", arr); }}
                className="w-10 h-10 rounded-lg bg-transparent border border-slate-700 cursor-pointer" />
            ))}
            <button onClick={() => set("brand_colors", [...(data.brand_colors || []), "#ffffff"])} className="w-10 h-10 rounded-lg border border-dashed border-slate-700 text-slate-500">+</button>
          </div>
        </Field>
        <Field label="Fonts"><Input className={inputCls} value={data.fonts} onChange={(e) => set("fonts", e.target.value)} /></Field>
        <Field label="Thumbnail style"><Input className={inputCls} value={data.thumbnail_style} onChange={(e) => set("thumbnail_style", e.target.value)} /></Field>
        <div className="sm:col-span-2">
          <Label className="text-slate-300 text-sm mb-1.5 block">Brand visual references</Label>
          {brandAssets.length === 0 ? (
            <p className="text-xs text-slate-600">None yet. Attach an image in the Command Center and choose "Save as brand asset" to add one here.</p>
          ) : (
            <div className="flex flex-wrap gap-3" data-testid="brand-assets-list">
              {brandAssets.map((a) => (
                <div key={a.id} className="w-28" data-testid={`brand-asset-${a.id}`}>
                  <a href={`${API}${a.url}`} target="_blank" rel="noreferrer">
                    <img src={`${API}${a.url}`} alt={a.filename || "brand asset"} className="w-28 h-28 object-cover rounded-lg border border-slate-700 hover:border-violet-400 transition-colors" />
                  </a>
                  <p className="text-[10px] text-slate-500 mt-1 leading-tight" data-testid={`brand-asset-source-${a.id}`}>{a.source}</p>
                  <AssetTagChips asset={a} onUpdated={loadBrand} />
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      <Section title="Format defaults" desc="Defaults applied to new videos." testid="section-format">
        <Field label="Default format">
          <Select value={data.default_format} onValueChange={(v) => set("default_format", v)}>
            <SelectTrigger className={inputCls} data-testid="field-format"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200"><SelectItem value="short">Short</SelectItem><SelectItem value="long">Long-form</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Aspect ratio">
          <Select value={data.aspect_ratio} onValueChange={(v) => set("aspect_ratio", v)}>
            <SelectTrigger className={inputCls} data-testid="field-aspect"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200"><SelectItem value="9:16">9:16 (vertical)</SelectItem><SelectItem value="16:9">16:9 (horizontal)</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Duration target (seconds)"><Input type="number" className={inputCls} value={data.duration_target} onChange={(e) => set("duration_target", Number(e.target.value))} /></Field>
        <Field label="Publishing cadence"><Input className={inputCls} value={data.publishing_cadence} onChange={(e) => set("publishing_cadence", e.target.value)} placeholder="3x / week" /></Field>
        <Field label="Timezone">
          <Select value={data.timezone} onValueChange={(v) => set("timezone", v)}>
            <SelectTrigger className={inputCls} data-testid="field-timezone"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </Section>

      <Section title="Research" desc="Sources that fuel ideation." testid="section-research">
        <Field label="Competitor channels (comma separated)" full><ListField value={data.competitor_channels} onChange={(v) => set("competitor_channels", v)} placeholder="MKBHD, Mrwhosetheboss" /></Field>
        <Field label="Research sources (comma separated)" full><ListField value={data.research_sources} onChange={(v) => set("research_sources", v)} placeholder="Google Trends, The Verge" /></Field>
      </Section>

      <Section title="Budget" desc="Spend guardrails per channel." testid="section-budget">
        <Field label="Budget per video"><Input type="number" className={inputCls} value={data.budget_per_video} onChange={(e) => set("budget_per_video", Number(e.target.value))} data-testid="field-budget-per-video" /></Field>
        <Field label="Monthly budget"><Input type="number" className={inputCls} value={data.monthly_budget} onChange={(e) => set("monthly_budget", Number(e.target.value))} /></Field>
        <Field label="Currency">
          <Select value={data.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger className={inputCls} data-testid="field-currency"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{["USD", "EUR", "GBP", "AZN", "TRY"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </Section>

      <Section title="Automation" desc="Human oversight and automation toggles." testid="section-automation">
        <Toggle label="Human approval required before publish" checked={data.require_human_approval} onChange={(v) => set("require_human_approval", v)} testid="toggle-human-approval" />
        <Toggle label="Auto research topics" checked={data.auto_research} onChange={(v) => set("auto_research", v)} testid="toggle-auto-research" />
        <Toggle label="Auto write scripts" checked={data.auto_script} onChange={(v) => set("auto_script", v)} testid="toggle-auto-script" />
        <Toggle label="Auto publish (not recommended)" checked={data.auto_publish} onChange={(v) => set("auto_publish", v)} testid="toggle-auto-publish" />
      </Section>

      <Section title="Idea Radar" desc="Weekly AI ideas dropped into this channel's backlog. Channels stay isolated." testid="section-radar">
        <Toggle label="Enable Idea Radar" checked={data.idea_radar_enabled} onChange={(v) => set("idea_radar_enabled", v)} testid="toggle-idea-radar" />
        <div />
        <Field label="Preferred weekday (channel time)">
          <Select value={String(data.radar_weekday)} onValueChange={(v) => set("radar_weekday", Number(v))}>
            <SelectTrigger className={inputCls} data-testid="radar-weekday"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Hour (0–23)">
          <Input type="number" min={0} max={23} className={inputCls} value={data.radar_hour} onChange={(e) => set("radar_hour", Math.max(0, Math.min(23, Number(e.target.value))))} data-testid="radar-hour" />
        </Field>
        <Field label="Ideas per drop (1–5)">
          <Input type="number" min={1} max={5} className={inputCls} value={data.radar_count} onChange={(e) => set("radar_count", Math.max(1, Math.min(5, Number(e.target.value))))} data-testid="radar-count" />
        </Field>
      </Section>
    </>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-4 bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
      <span className="text-sm text-slate-300">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

const PUB_MODES = [
  { v: "private-upload", label: "Private upload", desc: "Upload + process; the video stays private." },
  { v: "review-before-publish", label: "Review before publish", desc: "Nothing publishes without your explicit approval (default)." },
  { v: "automatic", label: "Automatic", desc: "Approved + rendered videos auto-publish on schedule — no per-video approval." },
];
const PUB_TZS = ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Europe/Istanbul", "Asia/Baku", "Asia/Tokyo"];

function PublishingSettings({ channelId }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmAuto, setConfirmAuto] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/channels/${channelId}/publishing-settings`); setS(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [channelId]);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async (modeOverride, confirmed) => {
    const mode = modeOverride || s.mode;
    setSaving(true);
    try {
      const { data } = await api.put(`/channels/${channelId}/publishing-settings`, {
        ...s, mode, automatic_opt_in_confirmed: confirmed || mode === "automatic",
      });
      setS(data); toast.success("Publishing settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const pickMode = (v) => {
    if (v === "automatic" && s.mode !== "automatic") { setConfirmAuto(true); return; }
    set("mode", v);
  };

  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid="section-publishing">
      <Dialog open={confirmAuto} onOpenChange={setConfirmAuto}>
        <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-md" data-testid="automatic-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Enable automatic publishing?</DialogTitle>
            <DialogDescription className="text-slate-400">Approved, rendered videos will publish automatically on this channel's schedule.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-300">In automatic mode, APPROVED + rendered videos will publish to YouTube on this channel's schedule <span className="font-semibold text-amber-300">without per-video approval</span>. Every auto-publish is audited. You can pause it any time.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmAuto(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="automatic-confirm-cancel">Cancel</Button>
            <Button onClick={() => { setConfirmAuto(false); set("mode", "automatic"); save("automatic", true); }} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="automatic-confirm-enable">Enable automatic</Button>
          </div>
        </DialogContent>
      </Dialog>

      <h3 className="font-display font-semibold text-slate-100 mb-1">Publishing</h3>
      <p className="text-sm text-slate-500 mb-4">How videos leave this channel. Default is review-before-publish.</p>

      <div className="space-y-2 mb-4">
        {PUB_MODES.map((m) => (
          <label key={m.v} className={`flex items-start gap-3 border rounded-lg px-4 py-3 cursor-pointer ${s.mode === m.v ? "border-violet-500/50 bg-violet-500/5" : "border-slate-800 bg-slate-900/50"}`} data-testid={`publishing-mode-${m.v}`} onClick={() => pickMode(m.v)}>
            <span className={`w-4 h-4 rounded-full mt-0.5 flex-none border-2 ${s.mode === m.v ? "border-violet-400 bg-violet-400" : "border-slate-600"}`} />
            <div><p className="text-sm text-slate-200">{m.label}</p><p className="text-xs text-slate-500">{m.desc}</p></div>
          </label>
        ))}
      </div>

      <Toggle label="Pause auto-publish queue for this channel" checked={s.paused} onChange={(v) => set("paused", v)} testid="publishing-pause" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Timezone (IANA)</Label>
          <Select value={s.timezone} onValueChange={(v) => set("timezone", v)}>
            <SelectTrigger className={inputCls} data-testid="publishing-timezone"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{PUB_TZS.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Missed-schedule policy</Label>
          <Select value={s.late_publication_policy} onValueChange={(v) => set("late_publication_policy", v)}>
            <SelectTrigger className={inputCls} data-testid="publishing-late-policy"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="ask">Ask for a new time (never silently publish)</SelectItem>
              <SelectItem value="reject">Reject and require a new time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <Button onClick={() => save()} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="publishing-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save publishing settings</>}
        </Button>
      </div>
    </div>
  );
}
